import { useState } from "react";
import { Landmark } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, formatRupiah } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Th, Td, InjectBadge, WaLink } from "@/pages/admin/OrdersPanel";
import { ProofButton } from "@/components/admin/TxExtras";
import { useLiveRows, NewBadge } from "@/components/admin/LiveFeed";
import { CopyText } from "@/components/admin/CopyText";
import { ExportButton, cleanParams } from "@/components/admin/LogTools";
import {
  GAMES, PanelHeader, GameFilter, SearchBox, StatusPill, DecisionButtons, TableShell,
  usePersistedFilter, filterRows, countUnseen, bucketOf, fmtTime,
} from "@/components/admin/TxKit";

const BANK_TONE = {
  BCA: "from-sky-600 to-blue-800", BRI: "from-blue-600 to-indigo-900", BNI: "from-orange-500 to-teal-700", MANDIRI: "from-yellow-500 to-blue-900",
  BSI: "from-teal-500 to-emerald-800", DANA: "from-sky-400 to-blue-600", OVO: "from-violet-500 to-purple-800", GOPAY: "from-cyan-500 to-sky-700",
  SHOPEEPAY: "from-orange-500 to-red-600", SEABANK: "from-orange-400 to-amber-600", BANKJAGO: "from-amber-400 to-orange-600",
};

export const chipB = (b) => b.chip_b ?? (parseInt(String(b.chip_amount || "").replace(/\D/g, ""), 10) || 0);

const RekeningCard = ({ b }) => (b.bank ? (
  <div className="min-w-[220px] rounded-xl border border-[#e5b80b]/20 bg-gradient-to-br from-[#1a1710] via-[#121218] to-[#0d0d12] p-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]" data-testid={`rekening-${b.id}`}>
    <div className="flex items-center justify-between gap-2">
      <span className={`inline-flex items-center gap-1 rounded-md bg-gradient-to-r px-2 py-0.5 text-[10px] font-black tracking-wider text-white ${BANK_TONE[b.bank] || "from-zinc-500 to-zinc-700"}`}>
        <Landmark size={10} /> {b.bank}
      </span>
      <span className="text-[9px] uppercase tracking-[0.2em] text-[#e5b80b]/60">Rekening</span>
    </div>
    <CopyText value={b.rek_number} label="No. rekening" className="mt-2 font-mono text-[15px] font-bold tracking-[0.12em] text-white" testid={`rek-number-${b.id}`} />
    <div className="mt-0.5 truncate text-[11px] font-semibold uppercase tracking-wider text-zinc-400">a.n. {b.rek_name}</div>
  </div>
) : <span className="text-xs text-zinc-500">{b.note || "—"}</span>);

const BuybackRow = ({ b, isNew, canManage, decide }) => (
  <tr className={`border-t border-white/5 transition-colors hover:bg-white/[0.02] ${isNew ? "bg-emerald-500/[0.06]" : ""}`} data-testid={`buyback-row-${b.id}`}>
    <Td className="whitespace-nowrap text-xs font-semibold text-sky-300"><NewBadge show={isNew} id={b.id} />{b.product_name}</Td>
    <Td className="whitespace-nowrap"><CopyText value={b.game_id} label="ID" className="font-mono font-bold text-white" testid={`buyback-gameid-${b.id}`} /><InjectBadge row={b} />{b.nickname ? <div className="mt-0.5 text-[11px] font-semibold text-emerald-400" data-testid={`buyback-nick-${b.id}`}>✓ {b.nickname}</div> : null}</Td>
    <Td className="whitespace-nowrap">
      <div className="text-xl font-black leading-none text-[#ffd700]" data-testid={`buyback-chip-${b.id}`}>{chipB(b)}<span className="ml-0.5 text-sm text-[#e5b80b]">B</span></div>
      {b.amount_rp ? <div className="mt-1 text-[11px] text-zinc-400">{formatRupiah(b.amount_rp)}</div> : null}
    </Td>
    <Td><RekeningCard b={b} /></Td>
    <Td><WaLink wa={b.wa_number} /></Td>
    <Td><ProofButton proofId={b.proof_id} /></Td>
    <Td className="whitespace-nowrap text-xs text-zinc-500">{fmtTime(b.created_at)}</Td>
    <Td><StatusPill status={b.status} testid={`buyback-status-${b.id}`} /></Td>
    <Td>
      {canManage && bucketOf(b.status) === "pending" ? (
        <DecisionButtons id={b.id} onConfirm={() => decide(b.id, "completed")} onReject={() => decide(b.id, "rejected")} />
      ) : <span className="font-mono text-[11px] text-zinc-500">{b.decided_by || b.injected_by || "—"}</span>}
    </Td>
  </tr>
);

export const BuybacksPanel = () => {
  const { can } = useAuth();
  const canManage = can("buybacks", "manage");
  const { rows, newIds, load, markSeen } = useLiveRows("/admin/buybacks", "bongkar");
  const [game, setGame] = usePersistedFilter("game_filter_buybacks", "all");
  const [q, setQ] = useState("");
  const pickGame = (g) => {
    setGame(g);
    if (g !== "all") markSeen(g);
  };

  const decide = async (id, next) => {
    try {
      await api.patch(`/admin/buybacks/${id}`, { status: next });
      toast.success(next === "completed" ? "Bongkar dikonfirmasi" : "Bongkar ditolak");
      load();
    } catch (e) {
      toast.error(errMsg(e, "Gagal update"));
    }
  };

  const byGame = filterRows(rows, game, "all", "", []);
  const shown = filterRows(byGame, "all", "all", q, ["game_id", "wa_number", "id", "rek_number", "rek_name", "bank"]);
  const totalB = shown.reduce((s, b) => s + chipB(b), 0);
  const totalRp = shown.reduce((s, b) => s + (b.amount_rp || 0), 0);

  return (
    <section className="space-y-5" data-testid="admin-buybacks-panel">
      <PanelHeader title="Bongkar Chip Masuk" subtitle={`Kelola dan proses request bongkar ${game === "all" ? "semua game" : GAMES[game]}`} pending={byGame.length} />
      <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-white/5 bg-[#101017] p-4">
        <SearchBox value={q} onChange={setQ} placeholder="Cari Player ID, WhatsApp, rekening, nama…" />
        <GameFilter value={game} onChange={pickGame} unseen={countUnseen(rows)} />
        {canManage ? <ExportButton url="/admin/buybacks/export" params={cleanParams({ product_id: game === "all" ? "" : game, q })} testid="buybacks-export" /> : null}
      </div>
      {!canManage ? <div className="rounded-xl border border-white/5 bg-[#13131a] px-4 py-2 text-xs text-zinc-500" data-testid="buybacks-readonly-note">Mode lihat saja — Anda tidak bisa mengubah status.</div> : null}
      <TableShell shown={shown.length} total={byGame.length} testid="admin-buybacks-table">
        <thead className="bg-[#14141c] text-[11px] uppercase tracking-widest text-zinc-500">
          <tr><Th>Game</Th><Th>Player ID</Th><Th>Nominal</Th><Th>Rekening Member</Th><Th>WhatsApp</Th><Th>Bukti</Th><Th>Waktu</Th><Th>Status</Th><Th>Ubah Status</Th></tr>
        </thead>
        <tbody>
          {shown.length === 0 ? (
            <tr><td colSpan={9} className="p-10 text-center text-zinc-500" data-testid="buybacks-empty">Tidak ada request bongkar yang menunggu diproses.</td></tr>
          ) : shown.map((b) => <BuybackRow key={b.id} b={b} isNew={newIds.has(b.id)} canManage={canManage} decide={decide} />)}
        </tbody>
        {shown.length ? (
          <tfoot className="border-t border-white/10 bg-[#14141c] text-xs">
            <tr>
              <td colSpan={2} className="px-4 py-3 font-semibold uppercase tracking-widest text-zinc-500">Total ({shown.length} transaksi)</td>
              <td colSpan={7} className="px-4 py-3" data-testid="buybacks-total">
                <span className="text-lg font-black text-[#ffd700]">{totalB}B</span>
                <span className="ml-3 text-zinc-400">{formatRupiah(totalRp)}</span>
              </td>
            </tr>
          </tfoot>
        ) : null}
      </TableShell>
    </section>
  );
};
